import chroma from 'chroma-js';
import type { Palette, PaletteShade } from '../types/branding.types';

// Target lightness values for each shade (HSL lightness 0-1)
const SHADE_CONFIG: Record<PaletteShade, { lightness: number; saturationScale: number }> = {
  '50':  { lightness: 0.97, saturationScale: 0.3 },
  '100': { lightness: 0.93, saturationScale: 0.4 },
  '200': { lightness: 0.85, saturationScale: 0.5 },
  '300': { lightness: 0.76, saturationScale: 0.6 },
  '400': { lightness: 0.64, saturationScale: 0.8 },
  '500': { lightness: 0.50, saturationScale: 1.0 },
  '600': { lightness: 0.43, saturationScale: 1.05 },
  '700': { lightness: 0.35, saturationScale: 1.1 },
  '800': { lightness: 0.27, saturationScale: 1.1 },
  '900': { lightness: 0.20, saturationScale: 1.05 },
  '950': { lightness: 0.12, saturationScale: 1.0 },
};

const HEX_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Generate an 11-shade palette from a single hex color.
 * The input hex becomes shade 500.
 */
export function generatePalette(hex: string): Palette {
  if (!HEX_REGEX.test(hex)) {
    console.warn(`[palette-generator] Invalid hex "${hex}", using default`);
    hex = '#16C787'; // AB3 default primary
  }

  const base = chroma(hex);
  const [hue, baseSat] = base.hsl();
  const safeHue = Number.isNaN(hue) ? 0 : hue;

  const palette = {} as Palette;

  for (const [shade, config] of Object.entries(SHADE_CONFIG) as [PaletteShade, typeof SHADE_CONFIG[PaletteShade]][]) {
    if (shade === '500') {
      palette[shade] = base.hex();
      continue;
    }

    const targetSat = Math.min(1, baseSat * config.saturationScale);
    palette[shade] = chroma.hsl(safeHue, targetSat, config.lightness).hex();
  }

  return palette;
}

/**
 * Generate dark-mode overrides for shades 50-200.
 * Uses rgba() with the base color at low opacity, matching global.css dark mode pattern.
 */
export function generateDarkPalette(hex: string): Partial<Palette> {
  if (!HEX_REGEX.test(hex)) {
    hex = '#16C787';
  }

  const base = chroma(hex);
  const [r, g, b] = base.rgb();

  return {
    '50': `rgba(${r}, ${g}, ${b}, 0.08)`,
    '100': `rgba(${r}, ${g}, ${b}, 0.12)`,
    '200': `rgba(${r}, ${g}, ${b}, 0.18)`,
  };
}

/**
 * Check WCAG AA contrast ratio of a color against white.
 * Returns { ratio, passes } where passes = ratio >= 4.5
 */
export function checkContrast(hex: string): { ratio: number; passes: boolean } {
  try {
    const ratio = chroma.contrast(hex, '#ffffff');
    return { ratio: Math.round(ratio * 100) / 100, passes: ratio >= 4.5 };
  } catch {
    return { ratio: 0, passes: false };
  }
}

export function isValidHex(value: string): boolean {
  return HEX_REGEX.test(value);
}
